import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  aiRequestPreviewService,
  caseService,
  contractService,
  documentService,
  researchService,
} from '../services/api';
import '../styles/AiRequestPreview.css';

const actionLabels = {
  DRAFT: 'Generate Draft',
  RESEARCH: 'Find Similar Cases',
  DOCUMENT_REVIEW: 'Review Documents',
  CONTRACT_REVIEW: 'Review Contract',
};

export default function AiRequestPreview() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const request = useMemo(() => location.state || null, [location.state]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!request?.action || !request?.providerId) {
      setLoading(false);
      setError('This AI request preview is missing its action or provider. Return to the case and start again.');
      return;
    }

    const loadPreview = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await aiRequestPreviewService.preview(id, request);
        setPreview(response.data);
      } catch (err) {
        const responseMessage = typeof err.response?.data === 'string'
          ? err.response.data
          : err.response?.data?.message;
        setError(responseMessage || 'Unable to prepare the AI request preview.');
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
  }, [id, request]);

  const returnToCase = () => navigate(caseReturnPath(), {
    state: {
      aiContext: {
        tab: request?.returnTab,
        documentTab: request?.returnDocumentTab,
      },
    },
  });

  const caseReturnPath = () => {
    const params = new URLSearchParams();
    if (request?.returnTab) params.set('tab', request.returnTab);
    if (request?.returnDocumentTab) params.set('documentTab', request.returnDocumentTab);
    const query = params.toString();
    return `/case/${id}${query ? `?${query}` : ''}`;
  };

  const completionMessage = () => {
    switch (request?.action) {
      case 'DRAFT':
        return 'Draft generated. Review the draft before saving or sharing it.';
      case 'RESEARCH':
        return 'Similar-case research completed. The available matches are shown below.';
      case 'DOCUMENT_REVIEW':
        return 'Document review completed. The generated summary is attached to this case.';
      case 'CONTRACT_REVIEW':
        return 'Contract review completed. The summary is available on the contract row.';
      default:
        return 'AI request completed.';
    }
  };

  const continueToProvider = async () => {
    if (!request) return;
    setSubmitting(true);
    setError('');
    try {
      let response;
      if (request.action === 'DRAFT') {
        response = await caseService.generateCaseDraft(id, request.providerId);
      } else if (request.action === 'RESEARCH') {
        response = await researchService.generateResearchCases(id, request.providerId);
      } else if (request.action === 'DOCUMENT_REVIEW') {
        response = await documentService.reviewDocuments(id, {
          providerId: request.providerId,
          requestedByUserId: request.requestedByUserId,
          documentIds: request.documentIds,
          instructions: request.instructions,
        });
      } else if (request.action === 'CONTRACT_REVIEW') {
        response = await contractService.reviewContract(id, request.contractId, {
          providerId: request.providerId,
          instructions: request.instructions,
        });
      }
      if (request.action === 'CONTRACT_REVIEW' && response?.data?.reviewStatus === 'FAILED') {
        throw new Error(response.data.reviewSummary || 'Contract review provider request failed.');
      }
      navigate(caseReturnPath(), {
        state: {
          aiOutcome: {
            tab: request.returnTab,
            documentTab: request.returnDocumentTab,
            message: completionMessage(),
          },
        },
      });
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setError(responseMessage || err.message || 'The AI provider request failed. No additional action was submitted.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="ai-preview-page">
      <div className="ai-preview-header">
        <div>
          <p className="ai-preview-eyebrow">AI transparency review</p>
          <h1>Review AI Request</h1>
          <p>Confirm the prompt and case information before LibraLegal sends this request to the selected provider.</p>
        </div>
        <button type="button" className="secondary-button" onClick={returnToCase}>Back to Case</button>
      </div>

      {loading && <div className="ai-preview-status">Preparing the request preview...</div>}
      {error && <div className="ai-preview-error">{error}</div>}

      {preview && !loading && (
        <div className="ai-preview-content">
          <section className="ai-preview-summary">
            <div><span>Action</span><strong>{actionLabels[preview.action] || preview.action}</strong></div>
            <div><span>Provider</span><strong>{preview.providerName}</strong></div>
            <div><span>Model</span><strong>{preview.modelName || 'Provider default'}</strong></div>
            <div><span>Endpoint</span><strong className="ai-preview-endpoint">{preview.endpoint}</strong></div>
          </section>

          <section className="ai-preview-section">
            <h2>Information included</h2>
            <ul>
              {(preview.includedData || []).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
            </ul>
          </section>

          <section className="ai-preview-section">
            <h2>Prompt sent to provider</h2>
            <pre className="ai-preview-prompt">{preview.prompt}</pre>
          </section>

          <p className="ai-preview-notice">{preview.disclosureNotice}</p>

          <div className="ai-preview-actions">
            <button type="button" className="secondary-button" onClick={returnToCase}>Cancel</button>
            <button type="button" className="primary-button" onClick={continueToProvider} disabled={submitting}>
              {submitting ? 'Sending to Provider...' : `Continue: ${actionLabels[preview.action] || 'Run AI Request'}`}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
